import { Avatar, Card, Descriptions, Form, Image, Input, Select, Space, Tag, Typography, message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import UsersModal from "../../../components/Modal/UsersModal/UsersModal";
import SapaButton from "../../../ui/Button/Button";
import DashboardCard from "../../Dashboard/DashboardCard";
import { useLocation, useParams } from "react-router-dom";
import { formatAmount } from "../../../helpers/numbers";
import ActionHandler from "../../../components/ActionHandler/ActionHandler";
import Chip from "../../../components/Chip/Chip";
import { getUserInfo, updateUser } from "../../../redux/actions/user";
import { useDispatch, useSelector } from "react-redux";
import ProgressLoader from "../../../components/Loader/ProgressLoader";
import { selectUpdateUserLoading } from "../../../redux/selectors/userSelectors";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getFullName = (user) => {
  const profile = user?.user_profile || {};
  return profile?.full_name?.trim() || [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ") || "Unknown User";
};

const getKycPhoto = (user) => {
  const verified = Array.isArray(user?.kyc_verified_data) ? user.kyc_verified_data : [];
  return verified.find((item) => item?.photo)?.photo;
};

const detailItems = (record, fields) =>
  fields.map(([label, key]) => ({
    key: label,
    label,
    children: formatValue(typeof key === "function" ? key(record) : record?.[key]),
  }));

const UserOverview = () => {
  const location = useLocation();
  const { userId } = useParams();
  const [data, setData] = useState({});
  const [fullUser, setFullUser] = useState(location.state || null);
  const dispatch = useDispatch();
  const isLoading = useSelector(selectUpdateUserLoading);
  const [form] = Form.useForm();

  const hydrate = (stateData) => {
    setFullUser(stateData);
    const filteredData = {
      id: stateData?.id || userId,
      first_name: stateData?.user_profile?.first_name ?? "",
      middle_name: stateData?.user_profile?.middle_name ?? "",
      last_name: stateData?.user_profile?.last_name ?? "",
      birth_date: stateData?.user_profile?.birth_date ?? "",
      gender: stateData?.user_profile?.gender ?? "",
      phone_number: stateData?.phone_number ?? "",
      email: stateData?.email ?? "",
      status: stateData?.status ?? "active",
      balance: stateData?.wallet?.main_balance,
      withdrawable_balance: stateData?.wallet?.withdrawable_balance,
      commission_balance: stateData?.wallet?.commission_balance,
      payout_balance: stateData?.wallet?.payout_balance,
      total_transactions: stateData?.total_transactions ?? 0,
      current_tier: stateData?.tier,
    };

    setData(filteredData);
    form.setFieldsValue(filteredData);
  };

  useEffect(() => {
    if (location.state) {
      hydrate(location.state);
      return;
    }

    if (userId) {
      dispatch(getUserInfo(userId))
        .then((res) => hydrate(res?.data || res))
        .catch((err) => message.error(err));
    }
    // form is stable from Ant; hydrate writes current user into it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.state, userId]);

  const kycPhoto = getKycPhoto(fullUser);
  const initials = useMemo(() => getFullName(fullUser).split(" ").map((part) => part[0]).join("").slice(0, 2), [fullUser]);

  const handleSaveChanges = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...data, ...values, id: data?.id || userId };
      await dispatch(updateUser(payload));
      message.success("User profile updated.");
      setData(payload);
      setFullUser((current) => ({
        ...current,
        ...payload,
        user_profile: {
          ...(current?.user_profile || {}),
          first_name: payload.first_name,
          middle_name: payload.middle_name,
          last_name: payload.last_name,
          birth_date: payload.birth_date,
          gender: payload.gender,
        },
      }));
    } catch (err) {
      message.error(err?.message || err || "Unable to update user.");
    }
  };

  const wallet = fullUser?.wallet || {};
  const virtualAccounts = Array.isArray(fullUser?.virtual_accounts) ? fullUser.virtual_accounts : [];
  const identityVerifications = Array.isArray(fullUser?.identity_verifications) ? fullUser.identity_verifications : [];
  const verifiedKyc = Array.isArray(fullUser?.kyc_verified_data) ? fullUser.kyc_verified_data : [];
  const pendingKyc = Array.isArray(fullUser?.kyc_pending_reviews) ? fullUser.kyc_pending_reviews : [];

  return (
    <ProgressLoader isLoading={isLoading}>
      <div className="users-overview">
        <Card className="users-overview__container">
          <div className="users-overview__profile-summary">
            <div className="users-overview__identity">
              {kycPhoto ? (
                <Image className="users-overview__photo" src={kycPhoto} alt={getFullName(fullUser)} />
              ) : (
                <Avatar className="users-overview__avatar">{initials}</Avatar>
              )}
              <div>
                <Typography.Title level={4}>{getFullName(fullUser)}</Typography.Title>
                <Space wrap>
                  <Tag color={data?.status === "active" ? "green" : "orange"}>{formatValue(data?.status).toUpperCase()}</Tag>
                  <Tag color={data?.current_tier ? "blue" : "default"}>Tier {data?.current_tier || "N/A"}</Tag>
                  <Typography.Text type="secondary">{fullUser?.account_id || "No account ID"}</Typography.Text>
                </Space>
              </div>
            </div>
            <ActionHandler actionButton={<Chip label="View all details" style={{ fontWeight: 700 }} />} actionButtonComponent={<UsersModal data={fullUser} />} />
          </div>

          <div className="users-overview__dashboard-card-container">
            <DashboardCard title="Main Wallet" value={formatAmount(wallet?.main_balance ?? "0.00", "₦")} showButton={true} record={data} />
            <DashboardCard title="Withdrawable" value={formatAmount(wallet?.withdrawable_balance ?? "0.00", "₦")} />
            <DashboardCard title="Commission" value={formatAmount(wallet?.commission_balance ?? "0.00", "₦")} />
            <DashboardCard title="Payout" value={formatAmount(wallet?.payout_balance ?? "0.00", "₦")} />
            <DashboardCard title="Total Transactions" value={data?.total_transactions} />
            <DashboardCard title="KYC Reviews" value={pendingKyc.length} />
          </div>

          <div className="users-overview__grid">
            <Card title="Editable Profile" className="users-overview__section">
              <Form form={form} layout="vertical" onFinish={handleSaveChanges}>
                <div className="users-overview__form-grid">
                  <Form.Item label="First Name" name="first_name">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Middle Name" name="middle_name">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Last Name" name="last_name">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Mobile Number" name="phone_number">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Email Address" name="email">
                    <Input type="email" />
                  </Form.Item>
                  <Form.Item label="Status" name="status">
                    <Select options={["active", "blocked", "suspended", "inactive"].map((value) => ({ value, label: value }))} />
                  </Form.Item>
                  <Form.Item label="Birth Date" name="birth_date">
                    <Input placeholder="YYYY-MM-DD" />
                  </Form.Item>
                  <Form.Item label="Gender" name="gender">
                    <Select allowClear options={["male", "female", "other"].map((value) => ({ value, label: value }))} />
                  </Form.Item>
                </div>
                <SapaButton loading={isLoading} label="Save Changes" htmlType="submit" />
              </Form>
            </Card>

            <Card title="Account Details" className="users-overview__section">
              <Descriptions column={1} size="small" items={detailItems(fullUser, [
                ["User ID", "id"],
                ["Account ID", "account_id"],
                ["Phone", "phone_number"],
                ["Email", "email"],
                ["Status", "status"],
                ["Guest User", "is_guest"],
                ["Created", "created_at"],
                ["Updated", "updated_at"],
              ])} />
            </Card>

            <Card title="Wallet Details" className="users-overview__section">
              <Descriptions column={1} size="small" items={detailItems(wallet, [
                ["Wallet ID", "id"],
                ["Main Balance", (item) => formatAmount(item?.main_balance || 0, "₦")],
                ["Withdrawable", (item) => formatAmount(item?.withdrawable_balance || 0, "₦")],
                ["Commission", (item) => formatAmount(item?.commission_balance || 0, "₦")],
                ["Payout", (item) => formatAmount(item?.payout_balance || 0, "₦")],
                ["Currency", "currency"],
              ])} />
            </Card>

            <Card title="Virtual Accounts" className="users-overview__section">
              {virtualAccounts.length ? virtualAccounts.map((account) => (
                <Descriptions key={account.id || account.account_number} column={1} size="small" items={detailItems(account, [
                  ["Bank", "bank_name"],
                  ["Account Name", "account_name"],
                  ["Account Number", "account_number"],
                  ["Provider", "provider"],
                  ["Permanent", "is_permanent"],
                ])} />
              )) : <Typography.Text type="secondary">No virtual accounts.</Typography.Text>}
            </Card>

            <Card title="Identity Verifications" className="users-overview__section">
              {identityVerifications.length ? identityVerifications.map((identity) => (
                <Descriptions key={identity.id || identity.identity_number} column={1} size="small" items={detailItems(identity, [
                  ["Type", "identity_type"],
                  ["Number", "identity_number"],
                  ["Created", "created_at"],
                ])} />
              )) : <Typography.Text type="secondary">No identity verifications.</Typography.Text>}
            </Card>

            <Card title="KYC Verified Data" className="users-overview__section users-overview__section--wide">
              {verifiedKyc.length ? verifiedKyc.map((kyc) => (
                <div className="users-overview__kyc-record" key={kyc.id}>
                  {kyc.photo && <Image width={96} src={kyc.photo} alt="KYC" />}
                  <Descriptions column={1} size="small" items={detailItems(kyc, [
                    ["Identity Type", "identity_type"],
                    ["Identity Number", "identity_number"],
                    ["First Name", "first_name"],
                    ["Last Name", "last_name"],
                    ["Date of Birth", "date_of_birth"],
                    ["Status", "status"],
                    ["Metadata", "metadata"],
                  ])} />
                </div>
              )) : <Typography.Text type="secondary">No verified KYC data.</Typography.Text>}
            </Card>
          </div>
        </Card>
      </div>
    </ProgressLoader>
  );
};

export default UserOverview;
